import { __decorate, __metadata } from "tslib";
import { Serialize, Type } from '@ftr/serialization';
import { LocalDate, LocalDateTime } from '@js-joda/core';
import { IsOptional } from 'class-validator';
import { Department } from '../../api/department';
import { TIMEFRAME_24HRS, TimeframeWithLocalTimes } from '../../api/shared';
import { Courtroom } from '../location';
import { AssetStatus } from './AssetStatus';
import { RecordingSegment } from './RecordingSegment';
/**
 * Details for playback of a recording
 */
export class RecordingWithSegments {
    /**
     * @param id of the recording
     * @param courtSystemId of the court system in which the recording was made
     * @param name The name of the recording
     * @param date The date the recording was recorded on
     * @param conversionStatus @deprecated (use recordingSegment.assetStatus)
     * @param courtroom in which the recording was made
     * @param segments Trm files or streams etc. which make up the recording
     * @param sealedSegments An array of non-overlapping timeframes that have been 'sealed'
     * @param recorderId The recording's recorder (live streams don't have one)
     * @param hasVideo does the recording also have video
     * @param audioTrackIsolated does the recording also have isolated audio tracks
     * @param department The department the recording belongs to
     * @param recordingType Type of recording, e.g. TRM, Stream
     * @param hasStt does the recording have some speech to text generated
     * @param hasMultiChannel does the recording have a multichannel playlist
     * @param audioChannelCount the number of audio channels in this recording
     * @param onTheRecordTimeframes the timesframes that this recording was on the record for
     * @param availableTimeframes the precise local times that the user can access in this recording (Stream only)
     * @param lastArchived the last date and time that this recording was archived (TRM only)
     * @param regionalLogSheetIds the regional logsheets associated with this recording
     * @param hasSoftwareAttributedStt does the recording have attributed speech to text generated
     * @param tags Features or attributes of this recording, eg 'stt', 'multichannel'
     */
    constructor(id, courtSystemId, name, date, conversionStatus, courtroom, segments, sealedSegments, recorderId, hasVideo, audioTrackIsolated, department, recordingType, hasStt = false, hasMultiChannel, audioChannelCount, onTheRecordTimeframes = [TIMEFRAME_24HRS], availableTimeframes = [], lastArchived, regionalLogSheetIds, hasSoftwareAttributedStt, tags) {
        this.id = id;
        this.courtSystemId = courtSystemId;
        this.name = name;
        this.conversionStatus = conversionStatus;
        this.sealedSegments = sealedSegments;
        this.recorderId = recorderId;
        this.hasVideo = hasVideo;
        this.audioTrackIsolated = audioTrackIsolated;
        this.recordingType = recordingType;
        this.hasStt = hasStt;
        this.hasMultiChannel = hasMultiChannel;
        this.audioChannelCount = audioChannelCount;
        this.onTheRecordTimeframes = onTheRecordTimeframes;
        this.regionalLogSheetIds = regionalLogSheetIds;
        this.hasSoftwareAttributedStt = hasSoftwareAttributedStt;
        this.date = date;
        this.courtroom = courtroom;
        this.segments = segments;
        this.department = department;
        this.availableTimeframes = availableTimeframes;
        this.lastArchived = lastArchived;
        this.tags = tags;
    }
}
__decorate([
    Serialize(),
    __metadata("design:type", LocalDate)
], RecordingWithSegments.prototype, "date", void 0);
__decorate([
    Type(() => Courtroom),
    __metadata("design:type", Object)
], RecordingWithSegments.prototype, "courtroom", void 0);
__decorate([
    Type(() => RecordingSegment),
    __metadata("design:type", Array)
], RecordingWithSegments.prototype, "segments", void 0);
__decorate([
    Type(() => Department),
    __metadata("design:type", Object)
], RecordingWithSegments.prototype, "department", void 0);
__decorate([
    Type(() => TimeframeWithLocalTimes),
    __metadata("design:type", Array)
], RecordingWithSegments.prototype, "availableTimeframes", void 0);
__decorate([
    Serialize(() => LocalDateTime),
    __metadata("design:type", Object)
], RecordingWithSegments.prototype, "lastArchived", void 0);
__decorate([
    Serialize(),
    IsOptional(),
    __metadata("design:type", Set)
], RecordingWithSegments.prototype, "tags", void 0);
/**
 * Derives the recordings asset status from the segments within it.
 */
export function getRecordingAssetStatus(recording) {
    const statuses = new Set(recording.segments.map(x => x.assetStatus));
    if (statuses.has(AssetStatus.Exists)) {
        return AssetStatus.Exists;
    }
    if (statuses.has(AssetStatus.Generating)) {
        return AssetStatus.Generating;
    }
    if (statuses.has(AssetStatus.Missing)) {
        return AssetStatus.Missing;
    }
    // Either there are no segments, or all of them are broken
    return AssetStatus.Broken;
}
export function mapRecordingWithSegmentsToPermissionResource(recording) {
    var _a, _b;
    return {
        id: recording.id,
        courtSystemId: recording.courtSystemId,
        locationId: (_a = recording.courtroom) === null || _a === void 0 ? void 0 : _a.id,
        departmentId: (_b = recording.department) === null || _b === void 0 ? void 0 : _b.id,
    };
}
